import { AddCard, Archive, Business, LocationOn, LocalPharmacy, CalendarMonth, Build, AccountBalance, BeachAccess } from "@mui/icons-material";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import Users from "./Users/Users";
import UserLayout from "./Users/UserLayout";
import { UserContext } from "../../stores/User/User.provider";
import useStore from "../../helpers/useStore";
import { PatientList } from "./PatientList";
import PatientArchive from "./PatientArchive/PatientArchive";
import { useSearchParams } from "react-router-dom";
import { TopLevelPaper } from "../../themes/StyledComponents";
import SublocalitiesManager from "./Sublocalities/SublocalitiesManager";
import KostentraegerManager from "./Kostentraeger/KostentraegerManager";
import TherapiesManager from "./Therapies/TherapiesManager";
import LocationsAdminManager from "./LocationsAdmin/LocationsAdminManager";
import PublicHolidayCalendar from "./PublicHolidays/PublicHolidayCalendar";
import MaintenanceUtilitiesManager from "./MaintenanceUtilities/MaintenanceUtilitiesManager";
import LocationAbsences from "./Absences/LocationAbsences";
import useChiefDebugAccess from "../../hooks/useChiefDebugAccess";
import type { UserDto } from "../../api-client";

interface AdminState {
  patientTabIndex: number;
  tab: string;
  /** The user currently open in the detail view; `{}` while creating a new one. */
  selectedUser: UserDto | null;
  /**
   * Mount key for the detail view. Bumped only when the admin *opens* a
   * different record from the list, never when a sub-tab saves and hands back
   * an updated object — so an in-place save (including the one that first
   * persists a new user and gives it an id) does not remount the subtree.
   */
  selectionKey: number;
}

const Administration = observer(() => {
  // Introduce a new state variable for the selected location
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const canSeeMaintenance = useChiefDebugAccess();

  let [searchParams, setSearchParams] = useSearchParams();

  const tabSelection = searchParams.get("tabSelection");
  const userIdParam = searchParams.get("userId");

  let firstState: AdminState = {
    patientTabIndex: 1,
    tab: "user",
    selectedUser: null, // Add a state to keep track of the selected user
    selectionKey: 0,
  };

  // Deep link support: /admin?userId=... opens that user's detail view.
  if (userIdParam) {
    firstState.selectedUser = { id: userIdParam };
  }

  if (tabSelection === "patients") {
    firstState.patientTabIndex = 2;
    firstState.tab = "patients";
  } else if (tabSelection === "archive") {
    firstState.patientTabIndex = 3;
    firstState.tab = "archive";
  } else if (tabSelection === "sublocalities") {
    firstState.patientTabIndex = 5;
    firstState.tab = "sublocalities";
  } else if (tabSelection === "kostentraeger") {
    firstState.patientTabIndex = 10;
    firstState.tab = "kostentraeger";
  } else if (tabSelection === "therapies") {
    firstState.patientTabIndex = 6;
    firstState.tab = "therapies";
  } else if (tabSelection === "locations-admin") {
    firstState.patientTabIndex = 7;
    firstState.tab = "locations-admin";
  } else if (tabSelection === "holidays") {
    firstState.patientTabIndex = 9;
    firstState.tab = "holidays";
  } else if (tabSelection === "absences") {
    firstState.patientTabIndex = 11;
    firstState.tab = "absences";
  } else if (tabSelection === "maintenance" && canSeeMaintenance) {
    firstState.patientTabIndex = 8;
    firstState.tab = "maintenance";
  }

  const [state, setState] = useState(firstState);

  const [selectedLocation, setSelectedLocation] = useState<string>(user?.locationId || "");

  const clearUserIdParam = () => {
    if (!searchParams.get("userId")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("userId");
    setSearchParams(nextParams, { replace: true });
  };

  // Callback function to handle closing UserDetails
  const handleCloseUserDetails = () => {
    setState((prev) => ({ ...prev, selectedUser: null }));
    clearUserIdParam();
  };

  useEffect(() => {
    if (!userIdParam) return;
    setState((prev) =>
      prev.selectedUser?.id === userIdParam
        ? prev
        : { ...prev, patientTabIndex: 1, tab: "user", selectedUser: { id: userIdParam } }
    );
  }, [userIdParam]);

  useEffect(() => {
    setSelectedLocation(user?.locationId || "");
    // fetchData(user?.locationId || "");
  }, [user?.locationId]);

  useEffect(() => {
    if (!canSeeMaintenance && state.tab === "maintenance") {
      setState((prev) => ({ ...prev, patientTabIndex: 1, tab: "user" }));

      if (tabSelection === "maintenance") {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("tabSelection");
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [canSeeMaintenance, searchParams, setSearchParams, state.tab, tabSelection]);

  /**
   * Open a record from the list — either an existing user or an empty draft
   * (`{}`) from the "Neu" button. Bumps `selectionKey` so the detail view
   * remounts and cannot carry any state over from the previous user.
   */
  const handleOpenUser = (user: UserDto | null) => {
    setState((prev) => ({
      ...prev,
      selectedUser: user || null,
      selectionKey: prev.selectionKey + 1,
    }));
  };

  /**
   * A sub-tab saved and handed back the persisted user. This replaces the
   * object in place — same record, same mount — so unsaved state in the other
   * tabs and the post-save activation buttons survive.
   */
  const handleUserUpdated = (user: UserDto | null) => {
    setState((prev) => (user ? { ...prev, selectedUser: user } : { ...prev, selectedUser: null }));
  };
  
  // Callback function to handle selecting a user or opening UserDetails with an empty user
  const handleSelectUser = (user: any) => {
    // console.log("userinhandlesl", user);
    if (!user) {
      // If no user is passed, it means "Add User" button is clicked, open UserDetails with an empty user
      setState((prev) => ({ ...prev, selectedUser: null }));
      clearUserIdParam();
    } else {
      // If a user is passed, it means an existing user is selected
      setState((prev) => ({ ...prev, selectedUser: user }));
    }
  };

  return (
    <Grid spacing={2} sx={{ overflowY: "auto", height: "calc(100vh - 80px)" }}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            height: "70px",
          }}
        >
          <Tabs
            value={state.tab}
            aria-label="icon label tabs example"
            orientation="horizontal"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              iconPosition="start"
              icon={
                <AddCard
                  sx={{
                    fontWeight: state.patientTabIndex === 0 ? "bold" : "normal",
                  }}
                />
              }
              label="Benutzer"
              value="user"
              onClick={() => {
                setState({ ...state, patientTabIndex: 1, tab: "user" });
              }}
            />
            <Tab
              icon={
                <AddCard
                  sx={{
                    fontWeight: state.patientTabIndex === 0 ? "bold" : "normal",
                  }}
                />
              }
              label="Patienten"
              value="patients"
              onClick={() => {
                setSearchParams({ tabSelection: "patients" });
                setState({ ...state, patientTabIndex: 2, tab: "patients" });
              }}
            />
            <Tab
              icon={
                <Archive
                  sx={{
                    fontWeight: state.patientTabIndex === 0 ? "bold" : "normal",
                  }}
                />
              }
              label="Archiv"
              value="archive"
              onClick={() => {
                setSearchParams({ tabSelection: "archive" });
                setState({ ...state, patientTabIndex: 3, tab: "archive" });
              }}
            />
            <Tab
              icon={
                <AddCard
                  sx={{
                    fontWeight: state.patientTabIndex === 0 ? "bold" : "normal",
                  }}
                />
              }
              label="Zimmer"
              value="room"
              onClick={() => {
                setState({ ...state, patientTabIndex: 4, tab: "room" });
              }}
            />
            <Tab
              icon={
                <LocationOn
                  sx={{
                    fontWeight: state.patientTabIndex === 5 ? "bold" : "normal",
                  }}
                />
              }
              label="Stadtteile"    // What user sees on the tab
              value="sublocalities"      // What code uses to identify this tab
              onClick={() => {
                setSearchParams({ tabSelection: "sublocalities" });
                setState({ ...state, patientTabIndex: 5, tab: "sublocalities" }); 
              }}
            />
            <Tab
              icon={
                <AccountBalance
                  sx={{
                    fontWeight: state.patientTabIndex === 10 ? "bold" : "normal",
                  }}
                />
              }
              label="Kostenträger"
              value="kostentraeger"
              onClick={() => {
                setSearchParams({ tabSelection: "kostentraeger" });
                setState({ ...state, patientTabIndex: 10, tab: "kostentraeger" });
              }}
            />
            <Tab
              icon={
                <LocalPharmacy
                  sx={{
                    fontWeight: state.patientTabIndex === 6 ? "bold" : "normal",
                  }}
                />
              }
              label="Heilmittel"
              value="therapies"
              onClick={() => {
                setSearchParams({ tabSelection: "therapies" });
                setState({ ...state, patientTabIndex: 6, tab: "therapies" });
              }}
            />
            <Tab
              icon={
                <Business
                  sx={{
                    fontWeight: state.patientTabIndex === 7 ? "bold" : "normal",
                  }}
                />
              }
              label="Standorte"
              value="locations-admin"
              onClick={() => {
                setSearchParams({ tabSelection: "locations-admin" });
                setState({ ...state, patientTabIndex: 7, tab: "locations-admin" });
              }}
            />
            <Tab
              icon={
                <CalendarMonth
                  sx={{
                    fontWeight: state.patientTabIndex === 9 ? "bold" : "normal",
                  }}
                />
              }
              label="Feiertage"
              value="holidays"
              onClick={() => {
                setSearchParams({ tabSelection: "holidays" });
                setState({ ...state, patientTabIndex: 9, tab: "holidays" });
              }}
            />
            <Tab
              icon={
                <BeachAccess
                  sx={{
                    fontWeight: state.patientTabIndex === 11 ? "bold" : "normal",
                  }}
                />
              }
              label="Abwesenheiten"
              value="absences"
              onClick={() => {
                setSearchParams({ tabSelection: "absences" });
                setState({ ...state, patientTabIndex: 11, tab: "absences" });
              }}
            />
            {canSeeMaintenance && (
              <Tab
                iconPosition="start"
                icon={
                  <Build
                    sx={{
                      fontWeight: state.patientTabIndex === 8 ? "bold" : "normal",
                    }}
                  />
                }
                label="Wartung"
                value="maintenance"
                onClick={() => {
                  setSearchParams({ tabSelection: "maintenance" });
                  setState({ ...state, patientTabIndex: 8, tab: "maintenance" });
                }}
              />
            )}
            {/* {selectedPatients.map((patient, index) => (
              <Tab
                key={index + 1}
                icon={<Person3 />}
                label={ <span style={{ color: 'black', fontWeight:'bold'}}>{patient.lastName}</span>}
                onClick={() => handleTabChange(null, index + 1)}
                value={index + 1}
              />
            ))} */}
          </Tabs>
        </Box>
      </Grid>
      <TopLevelPaper>
        {/*
          A single-child container: `spacing` bought nothing here but a 16px
          top pad on the item, and `marginTop: 20px !important` overrode the
          negative margin that spacing normally cancels it with. Together they
          left ~36px of dead space between the paper's own padding and the
          tab content on every admin tab.
        */}
        <Grid container>
          {/* Add left margin to the Users component */}
          <Grid
            item
            xs={12}
            sx={{ marginLeft: "10px", width: "100%", border: "0px solid red", display: "flex" }}
          >
            {state.tab === "user" && !state.selectedUser && (
              <Users
                onSelectUser={handleOpenUser}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
              />
            )}
            {state.selectedUser && state.tab === "user" && (
              // Keyed on the selection, so opening a different user remounts the
              // whole detail subtree. No sub-tab can then carry ids or record
              // sets from the previously selected user into an API write.
              <UserLayout
                key={state.selectionKey}
                user={state.selectedUser}
                onClose={handleCloseUserDetails}
                onSelectUser={handleUserUpdated}
              />
            )}
            {state.tab === "patients" && <PatientList />}
            {state.tab === "archive" && <PatientArchive />}
            {state.tab === "sublocalities" && <SublocalitiesManager />}
            {state.tab === "kostentraeger" && <KostentraegerManager />}
            {state.tab === "therapies" && <TherapiesManager />}
            {state.tab === "locations-admin" && <LocationsAdminManager />}
            {state.tab === "holidays" && <PublicHolidayCalendar />}
            {state.tab === "absences" && <LocationAbsences />}
            {canSeeMaintenance && state.tab === "maintenance" && <MaintenanceUtilitiesManager />}
          </Grid>
        </Grid>
      </TopLevelPaper>
    </Grid>
  );
});

export default Administration;
